import type { ShelemHandResult } from "@bing/engine-shelem";
import type { ReactNode } from "react";
import { faDigits } from "../i18n/digits";
import { fa } from "../i18n/fa";
import { HakemChip } from "./hakem-chip";

/* ------------------------------------------------------------------ *
 * A FINISHED HAND'S RESULT, AS شلم WRITES IT (P1 Task 7) — shared by the hand-end card on the felt
 * (`screens/shelem-hand-end.tsx`) and the end of the match's card (`WinOverlay`: since card-98 item 11
 * the last hand's BOX, `LastHandBox`, which replaced P2-4's line), so the two can never word one hand
 * two ways. Numbers come ONLY from `lastHand` and the view (the brief); no React state, no card art
 * (the end card's module must not draw a card).
 * ------------------------------------------------------------------ */

/** A TOTAL as the score box and the cards draw it: Persian digits, a TRUE minus (U+2212) — drawn in a
 *  `dir="ltr"` span, so «−۱۲۰» reads with its sign on the LEFT, as a number does. */
export function signedScore(n: number): string {
  return n < 0 ? fa.shelem.minusPoints(faDigits(-n)) : faDigits(n);
}

/** A CHANGE: «+۱۵۰» / «−۲۴۰» — in a `dir="ltr"` span too. A zero is a plain «۰», never «+۰» (the
 *  hand-end card's defenders after a sweep or a «شلم» call made: the hand gave them nothing). */
export function signedDelta(n: number): string {
  if (n === 0) return faDigits(0);
  return n < 0 ? fa.shelem.minusPoints(faDigits(-n)) : fa.shelem.plusPoints(faDigits(n));
}

/** ALL TWELVE tricks — a sweep or a «شلم» call made: the hand's banner moment (P1-8). */
export const allTwelve = (last: ShelemHandResult): boolean =>
  last.outcome === "sweep" || last.outcome === "shelemMade";

/**
 * THE LAST HAND'S BOX (card-98 item 11) — the end card's last hand, replacing its cramped «دست آخر: …»
 * line. The operator: «replace the cramped «دست آخر: …» line with a clear small box — who the حاکم was,
 * what they bid, and each team's points gained or lost that hand. No separate card before it» (the
 * table draws no hand-end card at the match's end since: `useHandEndMoments`). It says what the
 * hand-end card says, in the same words and colours, without the four laid-down cards:
 *   · the caption «دست آخر»;
 *   · the حاکم — its «حاکم» chip and its name, in its team's colour — and what it bid: the bid in its
 *     chip (a call names itself, «شلم خوانده») and the outcome's words («قرارداد شد» / «کم آورد» /
 *     «شلم»), gold for all twelve, as the line drew them;
 *   · each team's points that hand, signed, ours first, each in its team's colour.
 * Numbers come ONLY from `lastHand`. `compact`: the end card's tight forms — less air, the same rows;
 * `bare`: its TIGHTER form — no caption and the least air (card-98 review E-I2: at 320×568 under
 * ×1.3 / ×1.5 the box ended up to 70 px under the card's fold).
 */
export function LastHandBox({
  last,
  you,
  hakemName,
  compact = false,
  bare = false,
}: {
  last: ShelemHandResult;
  you: number;
  /** The حاکم's name as this viewer reads it (the card's own `nameOf`). */
  hakemName: string;
  compact?: boolean;
  bare?: boolean;
}): ReactNode {
  const myTeam = you % 2;
  const tone = (team: number): string => (team === myTeam ? "text-primary" : "text-error");
  const declTeam = last.declaringTeam;
  const slam = allTwelve(last);
  return (
    <div
      data-testid="shelem-end-last-hand"
      data-outcome={last.outcome}
      role="group"
      aria-label={fa.shelem.lastHandTitle}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: bare ? 0 : compact ? 2 : 4,
        padding: bare ? "2px 8px" : compact ? "4px 10px" : "6px 12px",
        borderRadius: "var(--radius)",
        background: "var(--surface-2)",
        border: "1px solid var(--hairline)",
        lineHeight: 1.3,
        textAlign: "start",
      }}
    >
      {bare ? null : (
        <span style={{ fontSize: 11.5, color: "var(--hint)", fontWeight: 600 }}>
          {fa.shelem.lastHandTitle}
        </span>
      )}
      {/* who the حاکم was, and what it bid */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        <span
          data-last-hand="hakem"
          style={{ display: "flex", alignItems: "center", gap: 4, minWidth: 0, flex: 1 }}
        >
          <HakemChip />
          <span
            dir="auto"
            className={`m-0 min-w-0 truncate font-bold ${tone(declTeam)}`}
            style={{ fontSize: 13.5 }}
          >
            {hakemName}
          </span>
        </span>
        <span
          data-last-hand="bid"
          style={{ display: "flex", alignItems: "center", gap: 5, flex: "none" }}
        >
          {last.called ? null : (
            <span
              aria-label={fa.shelem.contractIs(faDigits(last.bid))}
              className="m-0 whitespace-nowrap rounded-full bg-surface-container px-1.5 py-px font-bold leading-tight text-on-surface-variant"
              style={{ fontSize: 12.5 }}
            >
              {faDigits(last.bid)}
            </span>
          )}
          <span
            className={`m-0 whitespace-nowrap font-extrabold ${slam ? "" : tone(declTeam)}`}
            style={{ fontSize: 13, color: slam ? "var(--accent)" : undefined }}
          >
            {fa.shelem.handResult[last.outcome]}
          </span>
        </span>
      </div>
      {/* each team's points that hand — ours first */}
      <div
        data-last-hand="points"
        style={{ display: "flex", justifyContent: "space-around", columnGap: 12, flexWrap: "wrap" }}
      >
        {[myTeam, 1 - myTeam].map((team) => (
          <span
            key={team}
            data-last-hand-team={team === myTeam ? "ours" : "theirs"}
            className={`font-bold ${tone(team)}`}
            style={{ display: "inline-flex", alignItems: "baseline", gap: 5, fontSize: 13 }}
          >
            <span>{team === myTeam ? fa.shelem.us : fa.shelem.them}</span>
            <span dir="ltr" className="font-extrabold" style={{ fontSize: 15 }}>
              {signedDelta(last.teamDelta[team] ?? 0)}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}
